import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { customerFacingPriceFormatter, priceFormatterFromCurrency } from '../utils';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { useLanguage } from '../../utils/GeneralUtils';
import { OrderPageIntent } from '../OrderPageIntent';
import { bookingFeeAmount, getDepositInfo, isBookingFeeOn, withBookingFee } from '../core/pricing';
import { getHoliday } from '../../utils/getHoliday';
import { asOrderPageData } from '../core/types';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { useIntentConfig } from '../intentConfig/useIntentConfig';
import { usePriceExplanation } from '../hooks/usePriceExplanation';

export const PriceSummary = ({ muted = false }: { muted?: boolean }) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { orderPageState } = useOrderPage();
  const { currentStepIndex } = useIntentConfig();
  const { orderPageIntent } = orderPageState;
  const { date } = orderPageState.dateAndTimeDetails;
  const { price } = orderPageState.details;
  const explanation = usePriceExplanation();
  const hasMultipleTicketsPricing = !!asOrderPageData(orderPageState.orderPage)?.objects?.find(
    (x) => x.type === 'multipleTickets',
  );

  const shouldShowPriceSummary =
    currentStepIndex === 0 &&
    orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD &&
    orderPageIntent !== OrderPageIntent.BUY_PASS &&
    !orderPageState.isWalkIn;

  const depositInfo = getDepositInfo({
    widgetOrderPageData: orderPageState.orderPage,
    details: orderPageState.details,
    dateDetails: orderPageState.dateAndTimeDetails,
    account: orderPageState.account,
  });

  const holidayName = date ? getHoliday(orderPageState.account.holiday_calendar, date, language) : undefined;

  // withTax omitted, so this always returns a plain string rather than the tax-note element.
  const money = (amount: number) => customerFacingPriceFormatter(amount, orderPageState.account);
  // The surcharge gets its own line below, so this rate has to exclude it. Otherwise the block reads
  // as a sum whose first line already contains its second, and nothing adds up to the total.
  const surchargeAmount = explanation.surcharge?.amount ?? 0;
  // Rounded up, so the share shown is never less than what the participant actually pays.
  const { account } = orderPageState;
  const isFeeOn = isBookingFeeOn(account);
  const perPerson = isFeeOn
    ? Math.round((withBookingFee(price - surchargeAmount, account) / explanation.persons) * 100) / 100
    : Math.ceil((price - surchargeAmount) / explanation.persons);
  const bookingFee = bookingFeeAmount(price, account);
  const unit = explanation.unitLabel?.[language];
  // When a real deposit line exists below, the total keeps its name — one deposit row is enough.
  const showAsDeposit =
    orderPageIntent !== OrderPageIntent.MONETARY_GIFT_CARD &&
    !!asOrderPageData(orderPageState.orderPage)?.show_price_as_deposit &&
    depositInfo === null;

  return shouldShowPriceSummary ? (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        columnGap: 2,
        rowGap: 0.25,
        lineHeight: 1.2,
        alignItems: 'baseline',
        opacity: muted ? 0.45 : 1,
      }}>
      {holidayName && (
        <Box sx={{ gridColumn: '1 / -1' }}>
          <OrderPageTypography variant={'h6'} bold>
            {holidayName}
          </OrderPageTypography>
        </Box>
      )}

      {!hasMultipleTicketsPricing && explanation.persons > 1 && (
        <>
          <OrderPageTypography variant={'h6'} style={{ opacity: 0.7 }}>
            {unit ? t('order_page.perUnitTicket', { unit }) : t('order_page.perTicket')}
          </OrderPageTypography>
          <OrderPageTypography variant={'h6'} style={{ opacity: 0.7 }}>
            {money(perPerson)}
          </OrderPageTypography>
        </>
      )}

      {explanation.surcharge && (
        <>
          <OrderPageTypography variant={'h6'} style={{ opacity: 0.7 }}>
            {t('order_page.priceWeekendSurcharge')}
          </OrderPageTypography>
          <OrderPageTypography variant={'h6'} style={{ opacity: 0.7 }}>
            {money(withBookingFee(explanation.surcharge.amount, account))}
          </OrderPageTypography>
        </>
      )}

      <OrderPageTypography variant={'h6'} style={{ opacity: 0.7 }}>
        {showAsDeposit ? t('order_page.deposit') : t('order_page.total')}
      </OrderPageTypography>
      <OrderPageTypography variant={'h3'} bold>
        {customerFacingPriceFormatter(withBookingFee(price, account), account, true)}
      </OrderPageTypography>

      {bookingFee > 0 && (
        <Box sx={{ gridColumn: '1 / -1' }}>
          <OrderPageTypography variant={'h6'} style={{ opacity: 0.7 }}>
            {t('order_page.includesBookingFee', { fee: money(bookingFee) })}
          </OrderPageTypography>
        </Box>
      )}

      {depositInfo !== null && (
        <>
          <OrderPageTypography variant={'h6'} style={{ opacity: 0.7 }}>
            {t('order_page.deposit')}
          </OrderPageTypography>
          <OrderPageTypography variant={'h6'} style={{ opacity: 0.7 }}>
            {priceFormatterFromCurrency(
              withBookingFee(depositInfo.amount, account),
              depositInfo.currency,
              undefined,
              false,
              false,
              isFeeOn ? 2 : 0,
            )}
          </OrderPageTypography>
        </>
      )}
    </Box>
  ) : null;
};
